import type { Dictionary } from "./pt";

/**
 * Inglês. Tipado como `Dictionary` ⇒ o compilador recusa chave faltando ou sobrando.
 * Vocabulário de métrica segue o do Gerenciador de Anúncios da Meta (Spend, Reach,
 * Frequency, ThruPlay…) — o revisor precisa reconhecer os termos na tela.
 */
export const en: Dictionary = {
    common: {
        cancel: "Cancel",
        save: "Save",
        loading: "Loading...",
        connect: "Connect",
        disconnect: "Disconnect",
        activeSession: "Active session",
        myAccount: "My account",
        adminSystem: "Administration & System",
        signOut: "Sign out",
        default: "Default",
        connectedAccounts: "Connected accounts",
        needsAttention: "Needs attention",
        switchingStore: "Switching store",
        storeSwitchFailed: "Could not switch store",
        storeSwitchFailedHint: "Try again; if it keeps happening, sign out and back in.",
        allGood: "All good",
        last30Days: "Last 30 days",
        activeCampaignsSum: "Summed across accounts",
        searchAccount: "Search account, ID or currency...",
        language: "Language",
        portuguese: "Português",
        english: "English",
    },

    nav: {
        // Seções
        "VISÃO GERAL": "OVERVIEW",
        OPERAÇÃO: "OPERATIONS",
        "LOGÍSTICA & SAC": "LOGISTICS & SUPPORT",
        "TRÁFEGO PAGO": "PAID TRAFFIC",
        "PROGRAMA DE INDICAÇÃO": "REFERRAL PROGRAM",
        "CAMPANHAS E AUTOMAÇÕES": "CAMPAIGNS & AUTOMATIONS",
        "CORE SAAS": "CORE SAAS",
        EQUIPE: "TEAM",
        FINANCEIRO: "BILLING",
        "SISTEMA & INFRA": "SYSTEM & INFRA",
        ATENDIMENTO: "SUPPORT",
        "MULPLY INTERNA": "MULPLY INTERNAL",
        // Itens
        "Guia Rápido": "Quick Start",
        Dashboard: "Dashboard",
        Pedidos: "Orders",
        "Rastreio e SAC": "Tracking & Support",
        Produtos: "Products",
        Clientes: "Customers",
        Chat: "Chat",
        "Rel. Atendimento": "Support Reports",
        "Config. Atendimento": "Support Settings",
        Transportadoras: "Carriers",
        Parceiros: "Partners",
        Wizard: "Wizard",
        Início: "Home",
        Diagnóstico: "Diagnosis",
        "Criativos & Ângulos": "Creatives & Angles",
        "Fábrica de Criativos": "Creative Factory",
        "Meta Ads": "Meta Ads",
        Configurações: "Settings",
        "Indique e Ganhe": "Refer & Earn",
        Disparos: "Broadcasts",
        "Modelos de E-mail": "Email Templates",
        "Meus Fluxos": "My Flows",
        "Galeria de Templates": "Template Gallery",
        Histórico: "History",
        Analytics: "Analytics",
        "Meu Plano": "My Plan",
        Integrações: "Integrations",
        Usuários: "Users",
        "Cargos & Permissões": "Roles & Permissions",
        Auditoria: "Audit Log",
        "Prazos de Entrega": "Delivery Deadlines",
        "Plugin de Rastreamento": "Tracking Plugin",
        "Email SMTP": "SMTP Email",
        Lojas: "Stores",
        "Usuários Globais": "Global Users",
        "Cargos Globais": "Global Roles",
        "Membros da Equipe": "Team Members",
        "Cargos da Plataforma": "Platform Roles",
        Faturamento: "Billing",
        Gateways: "Gateways",
        Pacotes: "Packages",
        Afiliados: "Affiliates",
        "Tarifas Meta": "Meta Rates",
        "Monitor de Saúde": "Health Monitor",
        "Monitor de Infra": "Infra Monitor",
        "Gestão de Nós": "Node Management",
        Performance: "Performance",
        "Erros & Logs": "Errors & Logs",
        "Dados Demo": "Demo Data",
        "Mapa do Sistema": "System Map",
        Releases: "Releases",
        "Central de Ajuda": "Help Center",
        "Central de Suporte": "Support Center",
        // Achados pela trava de cobertura em 2026-08-11: `useNavLabel` é
        // `dict.nav[title] ?? title`, então chave faltando renderiza em PORTUGUÊS sem erro
        // nem teste vermelho. Estes 16 são de `/admin/super/*` — o revisor da Meta não os
        // alcança, mas o mecanismo que os deixou passar é o mesmo que deixaria passar um
        // item de menu de lojista.
        "Marketing Lojistas": "Merchant Marketing",
        "Disparos de Campanha": "Campaign Broadcasts",
        "Catálogo de E-mails": "Email Catalog",
        "E-mails da Plataforma": "Platform Emails",
        "Conexões da Mulply": "Mulply Connections",
        Atendimento: "Support",
        ECOSSISTEMA: "ECOSYSTEM",
        Automações: "Automations",
        "Permissões Chatwoot": "Chatwoot Permissions",
        "Regras de Copy": "Copy Rules",
        "Réguas do Diagnóstico": "Diagnostic Rulesets",
        "Roadmap Tráfego": "Traffic Roadmap",
        PLATAFORMA: "PLATFORM",
        "Provedores de Rastreio": "Tracking Providers",
        "Provedores de IA": "AI Providers",
        "SMTP Plataforma": "Platform SMTP",
    },

    billing: {
        planYearly: "annual",
        planMonthly: "monthly",
        day: "day",
        days: "days",
        trialExpired: (d: string) => `🔒 Access blocked: trial expired ${d} ago. Your orders are still being received, but you need a subscription to access them.`,
        subscribeNow: "Subscribe now",
        trialEndingCritical: "Avoid having your operation blocked — subscribe to a plan.",
        trialEndingSoft: "Subscribe to a plan to keep using Mulply.",
        trialEnds: (d: string, tail: string) => `Your trial ends in ${d}. ${tail}`,
        subscribePlanNow: "Subscribe to a plan",
        renewsIn: (plan: string, d: string) => `Your${plan} plan renews in ${d}. Make sure your payment details are up to date.`,
        viewBilling: "View billing",
        pastDue: "Your subscription payment failed. Update your payment details to avoid service suspension.",
        updatePayment: "Update payment",
        canceled: "Your subscription was canceled. Reactivate it to keep using every feature.",
        reactivatePlan: "Reactivate plan",
        unpaid: "Your subscription is suspended for non-payment. Settle it now to restore access.",
        settlePayment: "Settle payment",
    },

    /** Ver o docblock em `pt.ts`. Nome de marca não se traduz. */
    paymentMethod: {
        PIX: "Pix",
        CARD: "Card",
        BOLETO: "Boleto",
        SHOPIFY_PAYMENTS: "Shopify Payments",
        PAYPAL: "PayPal",
        OTHER: "Other",
        UNKNOWN: "Unidentified",
    },

    charts: {
        noData: "No data",
        installments: "Installments",
        installmentsSub: "Installments — excludes Pix and canceled",
        installmentsRange: "5x or more",
        centerOrders: "orders",
        paymentMethods: "Payment methods",
        paymentMethodsSub: "Distribution by method — all orders",
        topCustomers: "Top VIP customers",
        topCustomersSub: "Highest LTV in the period",
        noCustomers: "No customer in the period",
        topProducts: "Top 5 products",
        topProductsSub: "Best sellers in the period",
        noProducts: "No product data",
        noSales: "No sales data in the period",
        dailyTotal: "Daily total",
        ordersByOrigin: "Order volume by origin",
    },

    sync: {
        allIntegrations: "all integrations",
        started: "Sync started!",
        startedBody: "You will be notified when the data is updated.",
        nothingToSync: "Nothing to sync",
        errorTitle: "Error",
        nothingToSyncBody:
            "No connected platform pulls history — sales from these platforms arrive automatically, in real time.",
        syncAll: "Sync all",
        syncOne: (nome: string) => `Sync ${nome}`,
        inQueue: "A sync is already running in the queue. Please wait...",
        sync: "Sync",
        startedTitle: "Sync started",
        startedLong: "Your request was sent. You can close this window and keep working — the new data shows up in your dashboard in a few moments.",
        close: "Close",
        manualTitle: "Manual sync",
        descAll: (nome: string) => `Fetches orders from ${nome} active for the selected period.`,
        descOne: (nome: string) => `Fetches the most recent orders from ${nome} for the selected period.`,
        periodAll: "Period: all dates",
        period: (intervalo: string) => `Period: ${intervalo}`,
        until: "to",
        starting: "Starting...",
        availableIn: (s: string) => `Available in ${s}s`,
        start: "Start sync",
    },

    chat: {
        userList: "User list",
        roomStart: "Start of this room",
        sayHi: "Say hi to the team!",
        retention: (d: string) => `Messages in this chat are kept for ${d} days for security.`,
        internalNote: "Internal conversation — visible to your team only",
    },

    currency: {
        title: "Exchange rates",
        vsFriday: "vs. Friday close",
        vsYesterday: "vs. yesterday's close",
        refresh: "Refresh rates",
    },

    chrome: {
        helpCenter: "Help center",
        helpOverview: "Overview",
        helpDocs: "Documentation",
        notifications: "Notifications",
        fullScreen: "View full screen",
        navMenu: "Navigation menu",
        supportMode: "SUPPORT MODE (GOD MODE) ACTIVE: every action taken in this session is being recorded in the Audit Log.",
    },

    tracking: {
        unavailableTitle: "Mulply automatic tracking is currently down",
        unavailableBody: "Your orders stay in the queue and will resume updating once the service is back. No action is needed and there is no need to open a ticket.",
        unavailableLink: "See details",
    },
    login: {
        title: "Welcome back",
        subtitle: "Access your operations dashboard",
        email: "Email",
        password: "Password",
        signIn: "Sign in",
        signingIn: "Signing in...",
        forgotPassword: "Forgot?",
        hidePassword: "Hide password",
        showPassword: "Show password",
        noAccount: "Don't have an account?",
        signUp: "Create account",
        signInGoogle: "Sign in with Google",
        signInFacebook: "Sign in with Facebook",
        divider: "Or use your email",
        emailPlaceholder: "you@email.com",
        codeLabel: "Verification code",
        lostDevice: "Lost your device? Use one of your recovery codes.",
        backToEmail: "Back to email",
        firstTime: "First time here?",
        createAccount: "Create your account",
        accessGranted: "Access granted…",
        terms: "Terms",
        privacy: "Privacy",
        support: "Support",
        twoFactorTitle: "Two-Factor Authentication",
        twoFactorSubtitle: "Enter the code from your authenticator app",
        verify: "Verify",
        tooManyAttempts: "Too many sign-in attempts. Wait 1 minute and try again.",
    },

    trafego: {
        startHere: "Start here",
        startHereSub: "Tell us how your operation works — your answers calibrate every analysis",
        stepCaminho: "Path",
        stepVertical: "Operation",
        stepExpert: "Expert",
        stepMercado: "Market",
        stepPlataforma: "Platform",
        stepCheckout: "Checkout",
        stepNumeros: "Numbers",
        q1Title: "Do you already run paid traffic?",
        q1Sub: "Sets your starting point in the product.",
        q1Yes: "Yes, I already have campaigns",
        q1YesSub: "We will assess your whole operation: account, creatives, pixel and sales",
        q1No: "No, I want to start from scratch",
        q1NoSub: "Guided Builder assembles offer, creatives and campaign — under construction",
    },

    welcome: {
        chkIntegration: "Connect an integration",
        chkIntegrationDesc: "Link your Shopify or Yampi account to import orders.",
        chkRoles: "Set up roles",
        chkRolesDesc: "Define what your team can access in the system.",
        chkSettings: "Adjust settings",
        chkSettingsDesc: "Configure your store logo, name and details.",
        chkTeam: "Invite your team",
        chkTeamDesc: "Add your teammates so they can start working.",
        chkDone: (n: string) => `${n} completed`,
        badgeGuest: "Welcome to Mulply",
        badgeSuccess: "Success center",
        hello: "Hello,",
        traveler: "traveler",
        subGuest: "Your account was created, but we are still preparing your workspace.",
        subOwner: "Your store is almost ready to take off! Here is what is left to finish your setup.",
        subMember: "Here are a few tips to master your store operations today.",
        cardGuest: "Waiting for access",
        cardOwner: "Setup checklist",
        cardMember: "Quick access guide",
        cardSubGuest: "Ask your administrator to grant your access.",
        cardSubOwner: "Complete these steps to get the most out of the system.",
        cardSubMember: "Essential links for your daily routine.",
        nextStep: "What is the next step?",
        nextStepBody1: "Ask your administrator to assign a",
        role: "Role",
        nextStepBody2: "to your profile. Once saved, your access is granted instantly.",
        ctaTitle: "Suggestions or questions?",
        ctaBody: "Our support team is ready to help you set up your logistics operation.",
        helpCenter: "Help center",
        exploreTitle: "Explore features",
        exploreBody: "Click to learn more about each module.",
        tutOrders: "Order management",
        tutOrdersDesc: "Track your sales in real time, manage statuses and print labels.",
        tutLogistics: "Smart logistics",
        tutLogisticsDesc: "Direct integration with carriers and automatic tracking.",
        tutFinance: "Finance & metrics",
        tutFinanceDesc: "Detailed dashboards for revenue and performance.",
        tutTeam: "Team & collaboration",
        tutTeamDesc: "Create custom roles and manage your team.",
    },

    dashboardHome: {
        title: "Dashboard",
        subtitle: "A complete view of your operation in real time",
        fetchError: "Failed to fetch metrics",
        importStarted: "Starting the 2025 import...",
        importStartedBody: "This can take a few minutes. Wait for the completion notice.",
        importSuccess: "Success!",
        importSuccessBody: (n: string) => `2025 import finished. ${n} orders processed.`,
        apiError: "API error",
        errorTitle: "Error",
        importFailed: "Failed to import the history.",
        history2025: "2025 history",
        periodToday: "Today",
        periodYesterday: "Yesterday",
        periodWeek: "Week",
        periodMonth: "Month",
        periodYear: "Year",
        vsPrevPeriod: "vs previous period",
        vsPrevDay: "vs previous day",
        vsPrevWeek: "vs previous week",
        vsPrevMonth: "vs previous month",
        kpiGrossSales: "Gross sales",
        kpiPaidRevenue: "Paid revenue",
        kpiPaidRevenueMeta: "processed revenue",
        kpiAvgTicket: "Average order value",
        kpiAvgTicketMeta: "average per order",
        kpiAvgLtv: "Average LTV",
        kpiAvgLtvMeta: "revenue per customer",
        kpiCancelRate: "Cancel rate",
        kpiCancelRateMeta: "of total orders",
        kpiPending: "Pending",
        kpiPendingNone: "No order waiting",
        kpiPendingSome: "Paid and awaiting shipment",
        revenueChart: "Revenue trend",
        revenueChartSub: "Daily paid revenue in the period",
        funnel: "Conversion funnel",
        funnelSub: "Closing performance",
        funnelCreated: "CREATED",
        funnelCreatedSub: "Gross volume",
        funnelConfirmed: "CONFIRMED",
        funnelConfirmedSub: "Paid",
        conversionRate: "Conversion rate",
    },

    integrations: {
        title: "Integrations",
        subtitle: "Manage connections with e-commerce, payment and logistics platforms",
        requiresCampaignsModule: "Requires the Campaigns & Automations module",
        webhookOpenRiskFixable:
            "For now the URL is the only protection. Save the credential under Configure to accept only what the platform sends.",
        webhookOpenRiskPermanent:
            "Wbuy doesn't sign its events, so the URL is the key. Treat it like a password and turn this connection off when not in use.",
        configure: "Configure",
        webhookCopied: "Webhook URL copied!",
        loadError: "Failed to load integrations.",
        noPermissionStatus: "You do not have permission to change the status.",
        statusUpdateFailed: "Could not update the status.",
        statusUpdateError: "Error updating the status.",
        removed: "Integration removed.",
        removeError: "Failed to remove the integration.",
        reclassifyFailed: "Could not reclassify.",
        movedToCheckouts: "Moved to Checkouts.",
        movedToPlatforms: "Moved to Store platforms.",
        noSeparateCheckout: "No separate checkout",
        connectToStart: "Connect a platform to get started.",
        noActiveIntegration: "No active integration",
        notCountedInQuota: "Does not count toward the quota",
        markAsPlatform: "This is the store platform — move",
        markAsCheckout: "This is a separate checkout — move",
        available: "Available integrations",
        connect: "Connect",
        comingSoon: "Coming soon",
        comingSoonBody: "Not available yet — let support know if you use this platform.",
        planLocked: "is not available on this plan. Full centralization in a single professional hub.",
        comingSoonTitle: "Coming soon",
        comingSoonApis: "New APIs under review. Wait for the official release.",
        salesIntegrations: "Sales integrations",
        salesIntegrationsBody: "Sum of the Store platforms and Checkouts tabs. Inactive connections do not count.",
        storePlatforms: "Store platforms",
        storePlatformsBody: "Where your catalog lives. If the sale closes in the platform's own checkout, this is the only connection you need.",
        checkoutsBody: "Only for stores that close the sale outside the store platform. New connections start in the",
        checkoutsBodyTail: "— there you pick the role, and here you can move a connection that landed on the wrong tab.",
        paymentGateways: "Payment gateways",
        logistics: "Logistics",
        confirmDeleteTitle: "Confirm deletion",
        confirmDeleteBody: "Are you sure you want to remove this integration? This cannot be undone and may affect order syncing.",
        confirmDeleteYes: "Yes, remove",
        abandonedCart: "Abandoned cart",
        moving: "Moving...",
        removing: "Removing...",
        platformsTabName: "Store platforms",
        supportVia: "Customer support via",
        hubPrefix: "Hub",
        cartRealtime: "REAL TIME (WEBHOOK)",
        cartViaApi: "VIA API — EVERY 30 MIN",
        tabMetaAds: "Meta Ads",
    },

    metaAdsConnect: {
        galleryTitle: "Meta ad accounts",
        gallerySubtitle:
            "Connect your own Meta ad account to see your campaign reports inside Mulply.",
        connectButton: "Connect with Facebook",
        connecting: "Waiting for Facebook...",
        permissionNote:
            "Read-only access to your ad reports. You can disconnect at any time.",
        chooseAccount: "Choose the ad account",
        addAccount: "Add account",
        statusConnected: "Connected",
        statusSyncing: "Syncing...",
        statusNeedsReconnect: "Reconnection required",
        reconnect: "Reconnect",
        sync: "Sync",
        viewCampaigns: "View campaigns",
        openInAdsManager: "Open in Ads Manager",
        rename: "Rename",
        defaultAccount: "Default account",
        setDefault: "Set as default",
        remove: "Remove account",
        removed: "Account disconnected",
        selectAccountTitle: "Select ad account",
        selectAccountBody: "Choose which account to connect to Mulply.",
        renameTitle: "Rename account",
        renameBody: "Display name in the dashboard — nothing changes on Meta.",
        renamePlaceholder: "Account name",
        removeTitle: "Remove account?",
        removeBodyBefore: "The connection with",
        removeBodyAfter: "will be removed from Mulply. Nothing changes in your Meta account.",
        noAccountsFound: "No account found",
        adjustFilter: "Adjust your search or the status filter.",
        heroSubtitle: "Connect ad accounts to view your campaigns and metrics",
        syncAll: "Sync all",
        emptyTitle: "No ad account connected",
        emptyBody: "Connect your first Meta Ads account to track spend and campaigns.",
        emptyCta: "Connect Meta Ads",
        wait: "Please wait",
        syncNow: "Sync now",
        toastNoAppId: "Facebook App ID is not configured",
        toastPopupBlocked: "Popup blocked. Please allow popups for this site.",
        toastLoginCancelled: "Login cancelled or denied",
        toastFetchingAccounts: "Fetching ad accounts...",
        toastFetchError: "Failed to fetch accounts",
        toastNoAccounts: "No ad account found in this profile",
        toastConnectError: "Failed to connect",
        toastSaveError: "Failed to save the connection",
        toastSynced: "Accounts synced",
        toastRenamed: "Account renamed",
        toastRenameError: "Failed to rename",
        toastRemoveError: "Failed to remove",
        toastDefaultError: "Failed to set the default account",
        lastSync: "Last sync",
        moreActions: "More actions",
        investment30d: "Spend (30d)",
        tokenExpiredReconnect: "Token expired — reconnect",
        filterAll: "All",
        filterConnected: "Connected",
        spendLast30: "Spend (30 days)",
        activeCampaigns: "Active campaigns",
    },

    integrationVerticals: {
        ALL: "All",
        ECOMMERCE: "E-commerce",
        DROPSHIPPING: "Dropshipping",
        INFOPRODUTO: "Digital products",
        NUTRACEUTICO: "Nutraceutical",
    },

    integrationDescriptions: {
        SHOPIFY: "Leading global e-commerce platform.",
        YAMPI: "Transparent checkout and Brazilian online store.",
        WBUY: "Brazilian e-commerce and ERP platform (omnichannel).",
        NUVEMSHOP: "Leading e-commerce platform in Latin America (Tiendanube).",
        GURU: "Digital product checkout (Digital Manager Guru) — sales via webhook.",
        HOTMART: "Digital product marketplace and checkout — sales via webhook.",
        APPMAX: "Payment gateway with high conversion.",
        WIIO: "Sourcing and dropshipping platform.",
        SIHI: "Dropshipping and logistics agent.",
        ALIEXPRESS: "Global marketplace for sourcing.",
        SHOPEE: "Leading marketplace in Southeast Asia and Brazil.",
        MELHOR_ENVIO: "Shipping and parcel management.",
    },

    integrationTabs: {
        PLATAFORMAS: "Store platforms",
        CHECKOUTS: "Checkouts",
        GATEWAY: "Payments",
        SOURCING: "Suppliers",
        LOGISTICS: "Logistics",
        ATENDIMENTO: "Customer support",
        META_ADS: "Meta Ads",
    },

    metaAds: {
        pageTitle: "Meta Ads",
        pageSubtitle: "Campaigns and metrics from your ad accounts",
        emptyTitle: "No ad account connected",
        emptyBody: "Connect a Meta ad account on the Integrations page to see your reports here.",
        presetToday: "Today",
        presetYesterday: "Yesterday",
        presetWeek: "Week",
        presetMonth: "Month",
        presetYear: "Year",
        kpiSpend: "Total Spend",
        kpiSpendTip: "Total amount invested in the selected period",
        kpiVsPeriod: "Change vs.",
        kpiImpressions: "Impressions",
        kpiImpressionsTip: "Number of times your ads were displayed",
        kpiClicks: "Clicks",
        kpiClicksTip: "Total clicks on your ads (all types). CTR = Clicks ÷ Impressions",
        kpiReach: "Reach",
        kpiReachTip: "Number of unique people who saw at least one ad",
        kpiCpcSub: "Cost per click",
        kpiCpcTip: "Average cost per click — Spend ÷ Clicks",
        kpiCpmSub: "Per 1,000 impressions",
        kpiCpmTip: "Cost per 1,000 impressions — delivery efficiency",
        kpiCppSub: "Per 1,000 people reached",
        kpiCppTip: "Cost per 1,000 people reached — efficiency vs unique audience",
        kpiFrequency: "Frequency",
        kpiFrequencySub: "Impressions per person",
        kpiFrequencyTip:
            "Average number of times each person saw your ads. Above 3–4 may indicate creative fatigue",
        tabAccount: "Account",
        tabCampaigns: "Campaigns",
        tabAdSets: "Ad sets",
        tabAds: "Ads",
        clickToPause: "Click to pause",
        clickToActivate: "Click to activate",
        statusActive: "Active",
        statusPaused: "Paused",
        statusArchived: "Archived",
        saveFilters: "Save current filters",
        columns: "Columns",
    },

    actionErrors: {
        notAuthenticated: "Not authenticated",
        storeNotFound: "Store not found",
        noIntegrationPermission: "You do not have permission to manage integrations",
        connectionNotFound: "Connection not found in this store",
        invalidConnection: "Invalid connection",
        sessionExpired: "Connection session expired. Please connect with Facebook again.",
        tokenExpired: "Token expired",
    },

    metaAdsPanels: {
        selectedCount: "selected",
        activate: "Activate",
        pause: "Pause",
        bulkToggleFailed: (n: number) => `${n} item(s) could not be updated`,
        toggleFailed: (motivo: string) => `Could not change status: ${motivo}`,
        objTraffic: "Traffic",
        objConversions: "Conversions",
        rankAbove: "Above",
        rankAverage: "Average",
        adPreview: "Ad preview",
        copyAdName: "Copy ad name",
        video: "Video",
        noPreview: "No preview available",
        impressions: "Impressions",
        noMetricsPeriod: "No metrics for the selected period",
        noRankingData: "No data: Meta requires ≥ 500 impressions in the last 35 days of active delivery",
        metricsGuide: "Metrics guide",
        midRange: "Mid range",
        twoAdsMin: "≥ 2 ads",
        relevanceRankings: "Relevance rankings",
        impressions500: "≥ 500 impressions",
        last35Days: "in the last 35 days of active delivery",
        convRankOnly: "available only in campaigns optimized for conversions",
        dashMeaning: "= not enough volume, or ad paused for more than 35 days",
        videoMetrics: "Video metrics",
        imageOrLink: "Image or link ad",
        noVideoCreative: "(no video creative)",
        videoNoViews: "Video delivered, but no views in the period",
        purchaseAttribution: "Purchase attribution",
        attributionWindow: "7 days after click, 1 day after view",
        campaignIdTip: "Unique campaign identifier in the Meta API",
        yesterdayFixed: "yesterday's data · fixed time",
        warning: "Warning",
        conversionFunnel: "Conversion funnel",
        avgLtv: "Average LTV",
        productNameTip: "Product name in orders with UTM source Meta in the period",
        unitsSoldTip: "Total units sold through Meta Ads in the period",
        productRevenueTip: "Total revenue generated by this product in orders attributed to Meta",
        productShareTip: "Share of this product in total revenue attributed to Meta Ads",
        cohortRetention: "Cohort retention",
        cohort: "Cohort",
        cohortTip: "Month when the customer made their first purchase through Meta Ads",
        size: "Size",
        sizeTip: "Number of customers acquired through Meta in this cohort month",
        m0Tip: "Acquisition month — always 100%",
        m1Tip: "% who made a new purchase 1–2 months after the first Meta purchase",
        m3Tip: "% who made a new purchase 2–5 months after the first Meta purchase",
        m6Tip: "% who made a new purchase 5–8 months after the first Meta purchase",
        creativeAnalysis: "Creative analysis",
        creative: "Creative",
        creativeTip: "Ad name in Meta Ads",
        campaign: "Campaign",
        campaignParentTip: "Parent campaign of the ad",
        spend: "Spend",
        spendAdTip: "Total investment in the period for this ad.",
        cpcTip: "Average cost per click in the period.",
        campaignUtmTip: "Orders with the UTM of this campaign in the period",
        noCampaignsPeriod: "No campaign found for this period",
        adSetSpendTip: "Total spent by the ad set in the period",
        ctrTip: "Click-through rate — Clicks ÷ Impressions",
        adSingular: "ad",
        adPlural: "ads",
        searchAd: "Search ad...",
        adIdTip: "Unique ad identifier in Meta Ads",
        videoAssetTip: "Identifier of the video asset used in this ad",
        viewInMetaAds: "View in Meta Ads →",
        howToRead: "How to read the table data",
        colorSystem: "Color system",
        topThird: "of the ad set — best relative performance",
        bottomThird: "of the ad set — worst relative performance",
        hookRateNote: "Hook Rate uses 3-Second Video Views — tracked for every campaign objective, unlike 2-Second Continuous, which only works with the Video Views objective.",
        clickToExpand: "Click to expand",
        clickToCollapse: "Click to collapse",
        sortActiveFirst: "Click to invert (Active first)",
        sortPausedFirst: "Click to invert (Paused first)",
        sortByStatus: "Sort by status (Active first)",
        noAdsFound: "No ad found",
        noData: "No data",
        insightsError: "Failed to fetch insights",
        unknownError: "Unknown error",
        noConnectionTitle: "No Meta Ads account connected",
        connectNow: "Connect now",
        filterNamePlaceholder: "Filter name...",
        integrationsMetaAds: "Integrations → Meta Ads",
        viewCampaigns: "View campaigns",
        campaignMapping: "Campaign mapping",
        utmOverride: "UTM override by Campaign ID",
        noActiveCampaignToMap: "No active campaign to map",
        campaignNameTip: "Campaign name in Meta Ads",
        utmForceTip: "Forces a specific utm_campaign value for this campaign, overriding automatic fuzzy matching",
        fuzzyPriority: "Takes priority over fuzzy name matching",
        mappingSaveError: "Failed to save the mapping",
        dailyBrief: "Daily email brief",
        sendTime: "Send time",
        briefSendError: "Failed to send the brief",
        topCampaigns: "Top campaigns",
        lowestRoas: "Lowest ROAS",
        realAttribution: "Real attribution",
        realAttributionTip: "Orders with Meta UTM in the period · ROAS = Prisma revenue ÷ Meta spend",
        funnelTip: "Conversion steps in the period via Meta Pixel: Clicks → Add to Cart → Checkout → Purchase",
        rfmTip: "RFM segmentation, churn risk and average LTV of customers who purchased through Meta in the period",
        churnRisk: "Churn risk",
        productsViaMeta: "Products via Meta Ads",
        productsTip: "Best-selling products in orders with UTM source Meta — sorted by revenue, top 10 by default",
        showLess: "Show less",
        partnerCampaigns: "Partner campaigns in the period",
        partnerCampaignsTip: "Partner campaigns overlapping the selected period and the Meta revenue attributed to each one via UTM",
        noUtmMatch: "no UTM match",
        cohortTableTip: "Repurchase rate by acquisition month through Meta Ads — M+1: 1–2 months, M+3: 2–5 months, M+6: 5–8 months after the first purchase",
        noRetentionData: "Retention data not available for the selected period",
        noCreativeFound: "No creative found for the period",
        ctrCreativeTip: "Click-through rate (Clicks ÷ Impressions). Available for any historical period.",
        roasCreativeTip: "Return on ad spend (Purchase revenue ÷ Spend). Available for any historical period. Requires a configured purchase pixel.",
        qualityRankTip: "Meta Quality Ranking: the creative's position vs competitors for the same target audience. Available only for ads in active delivery (does not work for historical periods).",
        engRankTip: "Engagement Rate Ranking: engagement rate compared to ads with the same objective. Available only for ads in active delivery (does not work for historical periods).",
        convRankTip: "Conversion Rate Ranking: conversion rate compared to competing ads with the same objective. Available only for ads in active delivery (does not work for historical periods).",
        scoreTip: "Composite score 0–6 pts: ABOVE_AVERAGE = 2, AVERAGE = 1, BELOW_AVERAGE = 0 — sum of Quality + Engagement + Conversion. Requires an ad in active delivery.",
        campaigns: "Campaigns",
        clickCampaignHint: "Click a campaign to see its ad sets",
        hideEmpty: "Hide empty rows",
        searchCampaign: "Search campaign...",
        realRevenueTip: "Real revenue from orders with Meta UTM (via Prisma)",
        realRoasTip: "Real ROAS = Prisma revenue ÷ Meta spend for this campaign",
        orders: "Orders",
        roasGapTip: "Gap between Meta Pixel ROAS and Real ROAS (Prisma). Positive = Meta underestimated; negative = Meta overestimated.",
        noCampaignMatchesFilter: "No campaign matches the filter",
        noAdSetForPeriod: "No ad set found for this period",
        searchAdSet: "Search ad set...",
        noAdSetMatchesFilter: "No ad set matches the filter",
        pickCampaignForAdSets: "Select a campaign to see its ad sets",
        goToCampaigns: "Go to Campaigns →",
        pickAdSetForAds: "Select an ad set to see its ads",
        tokenExpiredHere: "This account's token expired. Reconnect it in",
        frequencyAt: (v: string) => `Frequency at ${v} — creative fatigue risk. Consider refreshing your creatives.`,
        tokenExpiresIn: (d: string) => `Meta Ads token expires in ${d}.`,
        reconnect: "Reconnect",
        settings: "Settings",
        expiresIn: (d: string) => `Expires in ${d}`,
        day: "day",
        days: "days",
        saveMapping: "Save mapping",
        utmCoverage: (m: string, t: string) => `${m} of ${t} orders have a trackable Meta UTM`,
        cumulativeRate: ", with the cumulative rate between each step",
        ordersCount: (n: string) => `${n} orders`,
        loadMore: (n: string) => `Load ${n} more campaigns`,
        ofTotal: (a: string, b: string) => `(${a} of ${b})`,
        qualityCol: "Quality",
        engagementCol: "Engmt.",
        conversionCol: "Conv.",
        allObjectives: "All",
        objSales: "Sales",
        objLeads: "Leads",
        objEngagement: "Engagement",
        objApp: "App",
        objAwareness: "Awareness",
        objPageLikes: "Page likes",
        objReach: "Reach",
        objLinkClicks: "Link clicks",
        rankBelow10: "< 10%",
        colorNote: "Colors compare ads within the same ad set — a red value here can be green in another ad set. For CPC / CPA / CPV lower is better; for CTR / ROAS / Purchases higher is better. Requires ≥ 2 ads with data to activate.",
        rankRule1: "Require ≥ 500 impressions in the last 35 days of active delivery",
        rankRule2: "Conv. rank is available only in campaigns optimized for conversions",
        rankRule3: "— = not enough volume, or ad paused for more than 35 days",
        attributionNote: "Purchases · Revenue · CPA · ROAS depend on the Meta Pixel or Conversions API configured on the account. Default window: 7 days after click, 1 day after view. The window can be changed in the ad set settings.",
        fatigueFrequency: "Frequency",
        fatigueCritical: "⚡ Critical fatigue",
        fatigueDetected: "⚠️ Fatigue detected",
        frequencyUp: "Frequency ↑",
        configSaved: "Settings saved",
        configSaveError: "Failed to save settings",
    },

    metaAdsColumns: {
        spend: { label: "Spend", tooltip: "Total amount invested in the period" },
        impressions: { label: "Impressions", tooltip: "Times the ad was displayed" },
        clicks: { label: "Clicks", tooltip: "Clicks on the ad link" },
        ctr: { label: "CTR", tooltip: "Click-through rate — Clicks ÷ Impressions" },
        cpc: { label: "CPC", tooltip: "Average cost per link click" },
        reach: { label: "Reach", tooltip: "Unique people who saw the ad" },
        cpm: { label: "CPM", tooltip: "Cost per 1,000 impressions" },
        cpp: { label: "CPP", tooltip: "Cost per 1,000 people reached" },
        frequency: { label: "Frequency", tooltip: "Average number of times each person saw the ad" },
        unique_clicks: { label: "Unique clicks", tooltip: "Link clicks from unique people" },
        purchase_roas: { label: "ROAS", tooltip: "Return on ad spend for purchases (revenue ÷ spend)" },
        outbound_clicks: {
            label: "Outbound",
            tooltip: "Clicks that left Meta to your site (excludes internal interactions such as likes and comments)",
        },
        outbound_clicks_ctr: { label: "Outbound CTR", tooltip: "Outbound click rate — Outbound ÷ Impressions" },
        cost_per_outbound_click: { label: "Outbound CPC", tooltip: "Average cost per outbound click to your site" },
        purchase_count: { label: "Purchases", tooltip: "Purchases attributed to the ad in the selected period" },
        revenue: { label: "Revenue", tooltip: "Total value of attributed purchases" },
        cost_per_purchase: { label: "CPA", tooltip: "Cost per purchase — Total spend ÷ Purchases" },
        landing_page_views: {
            label: "LP views",
            tooltip: "People who clicked the link AND loaded the landing page. Requires the Meta pixel.",
        },
        lpv_rate: {
            label: "LPV rate",
            tooltip: "Landing page views ÷ Outbound clicks. Below 60% may indicate a slow or broken landing page.",
        },
        hook_rate: {
            label: "Hook rate",
            tooltip: "% of impressions that produced a 2s+ view. Measures the opening hook — above 30% is good.",
        },
        ret_25: { label: "Ret. 25%", tooltip: "% of viewers who watched 25% of the video (relative to 2s views)" },
        ret_50: { label: "Ret. 50%", tooltip: "% of viewers who watched 50% of the video (relative to 2s views)" },
        ret_75: { label: "Ret. 75%", tooltip: "% of viewers who watched 75% of the video (relative to 2s views)" },
        thruplay_rate: {
            label: "ThruPlay %",
            tooltip: "% of viewers who watched to the end or for 15s+ (relative to 2s views)",
        },
        cpv: { label: "CPV", tooltip: "Cost per view — Spend ÷ 2s+ views" },
        quality_ranking: { label: "Quality", tooltip: "Ad quality ranking against competitors for the same audience" },
        engagement_rate_ranking: {
            label: "Eng. rank",
            tooltip: "Engagement rate ranking against competitors for the same audience",
        },
        conversion_rate_ranking: {
            label: "Conv. rank",
            tooltip: "Conversion rate ranking against competitors for the same audience",
        },
        add_to_cart_count: { label: "Add to cart", tooltip: "Add-to-cart events attributed to the ad" },
        cost_per_add_to_cart: { label: "Cost / cart", tooltip: "Cost per add to cart — Spend ÷ Add to cart" },
        initiate_checkout_count: { label: "Checkouts", tooltip: "Checkouts initiated and attributed to the ad" },
        cost_per_initiate_checkout: { label: "Cost / checkout", tooltip: "Cost per initiated checkout" },
        view_content_count: { label: "View content", tooltip: "Product/content views attributed to the ad" },
        cost_per_view_content: { label: "Cost / view", tooltip: "Cost per content view" },
        lead_count: { label: "Leads", tooltip: "Leads attributed to the ad in the period" },
        cost_per_lead: { label: "CPL", tooltip: "Cost per lead — Spend ÷ Leads" },
        complete_registration_count: { label: "Registrations", tooltip: "Completed registrations attributed to the ad" },
        cost_per_complete_registration: { label: "Cost / reg.", tooltip: "Cost per completed registration" },
        video_plays: { label: "Video views", tooltip: "2s+ views. Main proxy for video plays across campaigns." },
        cost_per_video_play: { label: "Raw CPV", tooltip: "Cost per video play (2s+ view)" },
        thruplay_count: { label: "ThruPlays", tooltip: "Total ThruPlays (watched to the end or 15s+)" },
        post_reactions: { label: "Reactions", tooltip: "Total reactions (Like, Love, etc.) on the ad" },
        post_comments: { label: "Comments", tooltip: "Total comments on the ad" },
        post_shares: { label: "Shares", tooltip: "Total shares of the ad" },
        post_saves: { label: "Saves", tooltip: "Total posts saved from the ad" },
        post_engagements: {
            label: "Engagement",
            tooltip: "Total post interactions (reactions + comments + shares + clicks)",
        },
        unique_outbound_clicks: {
            label: "Unique outbound",
            tooltip: "Unique outbound clicks (deduplicated per person)",
        },
        cost_per_unique_outbound_click: {
            label: "Unique outbound CPC",
            tooltip: "Cost per unique outbound click",
        },
        website_purchases_value: {
            label: "Pixel revenue",
            tooltip: "Revenue from purchases tracked by the Meta Pixel",
        },
    },
};
